/**
 * DataDog RUM Configuration
 * 
 * Initializes DataDog Real User Monitoring (RUM) for the application
 * - React Router integration
 * - Error tracking
 * - Session replay
 * - Performance monitoring
 */

import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import { endpoints } from 'Core/defaultValues';
import versionJson from '../../../version.json';

let isInitialized = false;

/**
 * Initialize DataDog RUM
 * 
 * This should be called once at the application startup, before React renders
 */
export const initializeDataDog = () => {
    // Prevent double initialization
    if (isInitialized) {
        console.warn('DataDog RUM is already initialized');
        return;
    }

    // Only initialize if all required config is available
    if (!endpoints.DATADOG_APPLICATION_ID || !endpoints.DATADOG_CLIENT_TOKEN || !endpoints.DATADOG_SITE || !endpoints.DATADOG_SERVICE || !endpoints.DATADOG_ENV) {
        console.warn('DataDog RUM configuration missing. Required: applicationId, clientToken, site, service, env. Skipping initialization.');
        return;
    }

    try {
        const isLocalhost = window.location.hostname === 'localhost';

        // Initialize DataDog Logs first
        datadogLogs.init({
            clientToken: endpoints.DATADOG_CLIENT_TOKEN,
            site: endpoints.DATADOG_SITE,
            service: endpoints.DATADOG_SERVICE,
            env: endpoints.DATADOG_ENV,
            version: versionJson?.Version,
            forwardErrorsToLogs: true,
            sessionSampleRate: 100,
            forwardConsoleLogs: ['error'],
            useSecureSessionCookie: !isLocalhost,
            useCrossSiteSessionCookie: !isLocalhost
        });

        // Initialize DataDog RUM
        datadogRum.init({
            applicationId: endpoints.DATADOG_APPLICATION_ID,
            clientToken: endpoints.DATADOG_CLIENT_TOKEN,
            site: endpoints.DATADOG_SITE,
            service: endpoints.DATADOG_SERVICE,
            env: endpoints.DATADOG_ENV,
            
            // Specify a version number to identify the deployed version
            version: versionJson?.Version,
            
            // Session configuration
            sessionSampleRate: 100, // Capture 100% of sessions in development
            sessionReplaySampleRate: 20, // Record 20% of sessions for replay
            useSecureSessionCookie: !isLocalhost,
            useCrossSiteSessionCookie: !isLocalhost,

            // Privacy settings
            defaultPrivacyLevel: 'mask-user-input',
            
            // Manual view tracking via custom hook (auto-tracking not reliable)
            // Using useDataDogViewTracking hook instead for consistent results
            // FIXME Figure out why auto-view tracking is not reliable, fix it,
            //  and remove the manual view tracking hooks.
            trackViewsManually: true,

            // Performance tracking
            trackUserInteractions: true,
            trackResources: true,
            trackLongTasks: true,


            // Development settings
            silentMultipleInit: true,
            beforeSend: (event) => {
                // Filter out sensitive data if needed
                return event;
            }
        });

        isInitialized = true;
        console.log('DataDog RUM initialized successfully');
        
    } catch (error) {
        console.error('Failed to initialize DataDog RUM:', error);
    }
};

/**
 * Check if DataDog RUM is initialized
 * @returns {boolean}
 */
export const isDataDogInitialized = () => isInitialized;

/**
 * Add custom user context to DataDog
 * @param {Object} userContext - User information
 */
export const setDataDogUser = (userContext) => {
    if (isInitialized) {
        datadogRum.setUser(userContext);
    }
};

/**
 * Add custom attributes to DataDog
 * @param {string} key - Attribute key
 * @param {*} value - Attribute value
 */
export const addDataDogAttribute = (key, value) => {
    if (isInitialized) {
        datadogRum.addAttribute(key, value);
    }
};

/**
 * Start a custom view in DataDog
 * @param {string} name - View name
 * @param {Object} context - Additional context
 */
export const startDataDogView = (name, context = {}) => {
    if (isInitialized) {
        datadogRum.startView(name, context);
    }
};

/**
 * Add a custom action to DataDog
 * @param {string} name - Action name
 * @param {Object} context - Additional context
 */
export const addDataDogAction = (name, context = {}) => {
    if (isInitialized) {
        datadogRum.addAction(name, context);
    }
};

/**
 * Add an error to DataDog
 * @param {Error} error - Error object
 * @param {Object} context - Additional context
 */
export const addDataDogError = (error, context = {}) => {
    if (isInitialized) {
        datadogRum.addError(error, context);
    }
};

export default {
    initializeDataDog,
    isDataDogInitialized,
    setDataDogUser,
    addDataDogAttribute,
    startDataDogView,
    addDataDogAction,
    addDataDogError
};
